import { memo } from "react";
import { Box, Button, ButtonBase, Checkbox, IconButton, InputBase, Tooltip } from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { UnitSystem } from "../../../utils/units";
import ModelClassSelect from "./ModelClassSelect";
import { Sparkline } from "./FuelCurve";
import { McGroup, RowInfo, VtRow, fmtField, loadUnit, num, yearsTo } from "./vtModel";
import { BLUE, LINE, MUTED, TEAL, TEXT, btnGhost, btnSmall, cellSx, checkboxSx, chipSx, iconBtnSx } from "./vtTheme";

/** Columns: select, name, model class, max, rate, 90% at, curve, actions. */
export const VT_COLUMNS = "44px minmax(220px, 1fr) 196px 108px 100px 76px 104px 80px";

export interface RowChip {
  label: string;
  tone: "mod" | "new" | "del";
}

interface VtTableRowProps {
  info: RowInfo;
  chip: RowChip | null;
  selected: boolean;
  checked: boolean;
  groups: McGroup[];
  unit: UnitSystem;
  /** Load at the top of every sparkline, so rows compare. */
  yMax: number;
  onSelect: (key: string) => void;
  onCheck: (key: string) => void;
  onPatch: (key: string, patch: Partial<VtRow>, coalesce?: string) => void;
  onDuplicate: (key: string) => void;
  onDelete: (key: string) => void;
  onRestore: (key: string) => void;
}

function VtTableRow({ info, chip, selected, checked, groups, unit, yMax, onSelect, onCheck, onPatch, onDuplicate, onDelete, onRestore }: VtTableRowProps) {
  const { r, s, fields: f, errs } = info;
  const deleted = !!r.deleted;
  const name = r.name.trim() || "Untitled";
  const L = num(r.max);
  const k = num(r.rate);
  const valid = !errs.max && !errs.rate && Number.isFinite(L) && Number.isFinite(k);
  const edited = f.max || f.rate;
  const select = () => { if (!selected) onSelect(r.key); };

  return (
    <Box
      sx={{
        display: "grid", gridTemplateColumns: VT_COLUMNS, alignItems: "center", minHeight: 58, borderBottom: `1px solid ${LINE}`,
        bgcolor: selected ? "rgba(0,212,173,0.08)" : "transparent", transition: "background-color .1s",
        "&:hover": { bgcolor: selected ? "rgba(0,212,173,0.1)" : "rgba(255,255,255,0.035)" },
        "&:hover .vt-row-actions, &:focus-within .vt-row-actions": { opacity: 1 },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Checkbox size="small" sx={checkboxSx} checked={checked} disabled={deleted} onChange={() => onCheck(r.key)} inputProps={{ "aria-label": `Select ${name}` }} />
      </Box>
      <Box sx={{ px: "3px", minWidth: 0 }}>
        <ButtonBase
          onClick={select}
          title={r.name}
          sx={{ display: "flex", alignItems: "center", gap: 1.25, width: "100%", minHeight: 44, px: 1.25, py: 0.75, borderRadius: 1.75, justifyContent: "flex-start", textAlign: "left", fontSize: 13, fontWeight: 500, color: TEXT }}
        >
          <Box sx={{ width: 14, height: 14, borderRadius: 1, flex: "none", bgcolor: r.color, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.2)" }} />
          <Box
            component="span"
            sx={{
              flex: 1, minWidth: 0, lineHeight: 1.35, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
              textDecoration: deleted ? "line-through" : "none", color: deleted ? MUTED : TEXT,
            }}
          >
            {name}
          </Box>
          {chip && <Box component="span" sx={chipSx(chip.tone)}>{chip.label}</Box>}
        </ButtonBase>
      </Box>
      <Box sx={{ px: "3px", minWidth: 0 }}>
        <ModelClassSelect
          variant="cell"
          value={r.mc}
          groups={groups}
          mod={f.mc}
          struck={deleted}
          disabled={deleted}
          ariaLabel={`Model class for ${name}`}
          onFocus={select}
          onChange={(mc) => onPatch(r.key, { mc })}
        />
      </Box>
      {(["max", "rate"] as const).map((field) => (
        <Box key={field} sx={{ px: "3px", minWidth: 0 }}>
          <InputBase
            value={r[field]}
            disabled={deleted}
            onFocus={select}
            onChange={(e) => onPatch(r.key, { [field]: e.target.value }, `${field}:${r.key}`)}
            sx={cellSx({ mod: f[field], err: !!errs[field], num: true, struck: deleted })}
            inputProps={{
              inputMode: "decimal",
              "aria-label": field === "max" ? `Max fuel load for ${name}, ${loadUnit(unit)}` : `Accumulation rate for ${name}`,
              "aria-invalid": !!errs[field],
              title: errs[field] || (s && f[field] ? `Saved: ${fmtField(field, s[field], unit)}` : ""),
            }}
          />
        </Box>
      ))}
      <Box sx={{ pr: "13px", textAlign: "right", fontSize: 13, color: MUTED, fontVariantNumeric: "tabular-nums" }}>{valid ? yearsTo(k, 10) : "—"}</Box>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        {valid && (
          <Sparkline
            max={L}
            rate={k}
            yMax={yMax}
            stroke={deleted ? "rgba(255,255,255,0.3)" : edited ? BLUE : TEAL}
            fill={deleted ? "rgba(255,255,255,0.05)" : edited ? "rgba(90,169,236,0.12)" : "rgba(0,212,173,0.1)"}
          />
        )}
      </Box>
      <Box>
        <Box className="vt-row-actions" sx={{ display: "flex", justifyContent: "flex-end", gap: 0.25, pr: 1, opacity: selected || deleted ? 1 : 0, transition: "opacity .1s" }}>
          {deleted ? (
            <Button sx={{ ...btnGhost, ...btnSmall }} onClick={() => onRestore(r.key)}>Restore</Button>
          ) : (
            <>
              <Tooltip title="Duplicate"><IconButton sx={iconBtnSx} aria-label={`Duplicate ${name}`} onClick={() => onDuplicate(r.key)}><ContentCopyOutlinedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              <Tooltip title="Delete"><IconButton sx={iconBtnSx} aria-label={`Delete ${name}`} onClick={() => onDelete(r.key)}><DeleteOutlineIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// Rows keep the same draft object until edited, so most skip re-rendering
// while someone types elsewhere. The handlers read the tab's latest state.
export default memo(VtTableRow, (a, b) =>
  a.info.r === b.info.r && a.info.s === b.info.s && a.info.errs.max === b.info.errs.max && a.info.errs.rate === b.info.errs.rate
  && a.chip?.label === b.chip?.label && a.selected === b.selected && a.checked === b.checked
  && a.groups === b.groups && a.unit === b.unit && a.yMax === b.yMax);
